<div
  class="medr-service-config"
  [class.narrow-layout]="isNarrowLayout()"
>
  <div class="service-header mb-3">
    <h3 class="service-title">
      <kendo-svg-icon
        [icon]="shieldCheckIcon"
        class="me-2"
      ></kendo-svg-icon>
      mEDR Configuration
    </h3>
    <p class="service-subtitle text-muted">Endpoint Detection & Response Service Setup</p>
  </div>

  <form
    [formGroup]="medrForm"
    class="row g-3"
  >
    <!-- Service Selection Section -->
    <div class="col-12">
      <div class="config-section">
        <h5 class="section-title mb-3">
          <kendo-svg-icon
            [icon]="gearIcon"
            class="me-2"
          ></kendo-svg-icon>
          Select EDR Service
        </h5>
        <div class="row g-3">
          @for (service of serviceOptions; track service.value) {
            <div
              class="col-12"
              [class.col-xl-4]="!isNarrowLayout()"
            >
              <div
                class="service-option-card"
                [class.selected]="isServiceSelected(service.value)"
                (click)="selectService(service.value)"
                style="cursor: pointer"
              >
                <div class="service-option-header">
                  <kendo-svg-icon
                    [icon]="
                      service.value === 'edr-connect'
                        ? networkIcon
                        : service.value === 'edr-complete'
                          ? shieldIcon
                          : serverIcon
                    "
                    class="service-icon"
                  ></kendo-svg-icon>
                  <h6 class="service-name">{{ service.text }}</h6>
                </div>
                <p class="service-description">{{ service.description }}</p>
                <div class="selection-indicator">
                  @if (isServiceSelected(service.value)) {
                    <kendo-svg-icon
                      [icon]="infoCircleIcon"
                      class="selected-icon"
                    ></kendo-svg-icon>
                    <span class="selected-text">Selected</span>
                  }
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </div>

    <!-- EDR Connect Specific Fields -->
    @if (showEdrConnectFields()) {
      <div class="col-12">
        <div class="config-section">
          <h5 class="section-title mb-3">
            <kendo-svg-icon
              [icon]="networkIcon"
              class="me-2"
            ></kendo-svg-icon>
            EDR Connect Configuration
          </h5>
          <div class="row g-3">
            <!-- API Credentials Provided -->
            <div
              class="col-12"
              [class.col-lg-6]="!isNarrowLayout()"
            >
              <kendo-formfield>
                <kendo-label
                  [for]="apiCredentialsProvided"
                  text="API Credentials Provided *"
                ></kendo-label>
                <kendo-switch
                  formControlName="apiCredentialsProvided"
                  #apiCredentialsProvided
                  offLabel="No"
                  onLabel="Yes"
                ></kendo-switch>
                <kendo-formhint>
                  <kendo-svg-icon
                    [icon]="infoCircleIcon"
                    class="me-1"
                  ></kendo-svg-icon>
                  Confirm that API credentials have been provided for integration
                </kendo-formhint>
                @if (
                  medrForm.get('apiCredentialsProvided')?.invalid && medrForm.get('apiCredentialsProvided')?.touched
                ) {
                  <kendo-formerror>
                    <kendo-svg-icon
                      [icon]="exclamationTriangleIcon"
                      class="me-1"
                    ></kendo-svg-icon>
                    API credentials confirmation is required
                  </kendo-formerror>
                }
              </kendo-formfield>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- EDR Complete Specific Fields -->
    @if (showEdrCompleteFields()) {
      <div class="col-12">
        <div class="config-section">
          <h5 class="section-title mb-3">
            <kendo-svg-icon
              [icon]="shieldIcon"
              class="me-2"
            ></kendo-svg-icon>
            EDR Complete Configuration
          </h5>
          <div class="row g-3">
            <!-- Total Number Of Machines -->
            <div
              class="col-12"
              [class.col-lg-6]="!isNarrowLayout()"
            >
              <kendo-formfield>
                <kendo-label
                  [for]="totalMachines"
                  text="Total Number Of Machines *"
                ></kendo-label>
                <kendo-numerictextbox
                  formControlName="totalMachines"
                  #totalMachines
                  [min]="1"
                  [value]="medrForm.get('totalMachines')?.value || 0"
                  placeholder="Enter number of machines"
                  class="w-100"
                  (valueChange)="onTotalMachinesChange($event)"
                ></kendo-numerictextbox>
                <kendo-formhint>
                  <kendo-svg-icon
                    [icon]="infoCircleIcon"
                    class="me-1"
                  ></kendo-svg-icon>
                  Total number of endpoints to be protected
                </kendo-formhint>
                @if (medrForm.get('totalMachines')?.invalid && medrForm.get('totalMachines')?.touched) {
                  <kendo-formerror>
                    <kendo-svg-icon
                      [icon]="exclamationTriangleIcon"
                      class="me-1"
                    ></kendo-svg-icon>
                    Total machines is required and must be at least 1
                  </kendo-formerror>
                }
              </kendo-formfield>
            </div>

            <!-- Enable EDR Network Discovery (Ranger) -->
            <div
              class="col-12"
              [class.col-lg-6]="!isNarrowLayout()"
            >
              <kendo-formfield>
                <kendo-label
                  [for]="networkDiscovery"
                  text="Enable EDR Network Discovery (Ranger)"
                ></kendo-label>
                <kendo-switch
                  #networkDiscovery
                  formControlName="networkDiscovery"
                  offLabel="No"
                  onLabel="Yes"
                ></kendo-switch>
                <kendo-formhint>
                  <kendo-svg-icon
                    [icon]="infoCircleIcon"
                    class="me-1"
                  ></kendo-svg-icon>
                  <p>Licensing will be based on total count of machines</p>
                </kendo-formhint>
              </kendo-formfield>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- EDR Control Specific Fields -->
    @if (showEdrControlFields()) {
      <div class="col-12">
        <div class="config-section">
          <h5 class="section-title mb-3">
            <kendo-svg-icon
              [icon]="serverIcon"
              class="me-2"
            ></kendo-svg-icon>
            EDR Control Configuration
          </h5>
          <div class="row g-3">
            <!-- Total Number Of Machines -->
            <div
              class="col-12"
              [class.col-lg-6]="!isNarrowLayout()"
            >
              <kendo-formfield>
                <kendo-label
                  [for]="totalMachines"
                  text="Total Number Of Machines *"
                ></kendo-label>
                <kendo-numerictextbox
                  formControlName="totalMachines"
                  #totalMachines
                  [min]="1"
                  [value]="medrForm.get('totalMachines')?.value || 0"
                  placeholder="Enter number of machines"
                  class="w-100"
                  (valueChange)="onTotalMachinesChange($event)"
                ></kendo-numerictextbox>
                <kendo-formhint>
                  <kendo-svg-icon
                    [icon]="infoCircleIcon"
                    class="me-1"
                  ></kendo-svg-icon>
                  Total number of endpoints to be protected
                </kendo-formhint>
                @if (medrForm.get('totalMachines')?.invalid && medrForm.get('totalMachines')?.touched) {
                  <kendo-formerror>
                    <kendo-svg-icon
                      [icon]="exclamationTriangleIcon"
                      class="me-1"
                    ></kendo-svg-icon>
                    Total machines is required and must be at least 1
                  </kendo-formerror>
                }
              </kendo-formfield>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- EDR CMMC Specific Fields -->
    @if (showEdrCmmcFields()) {
      <div class="col-12">
        <div class="config-section">
          <h5 class="section-title mb-3">
            <kendo-svg-icon
              [icon]="shieldIcon"
              class="me-2"
            ></kendo-svg-icon>
            EDR-CMMC Configuration
          </h5>
          <div class="row g-3">
            <!-- Total Number Of Machines -->
            <div
              class="col-12"
              [class.col-lg-6]="!isNarrowLayout()"
            >
              <kendo-formfield>
                <kendo-label
                  [for]="totalMachines"
                  text="Total Number Of Machines *"
                ></kendo-label>
                <kendo-numerictextbox
                  formControlName="totalMachines"
                  #totalMachines
                  [min]="1"
                  [value]="medrForm.get('totalMachines')?.value || 0"
                  placeholder="Enter number of machines"
                  class="w-100"
                  (valueChange)="onTotalMachinesChange($event)"
                ></kendo-numerictextbox>
                <kendo-formhint>
                  <kendo-svg-icon
                    [icon]="infoCircleIcon"
                    class="me-1"
                  ></kendo-svg-icon>
                  Total number of endpoints to be protected
                </kendo-formhint>
                @if (medrForm.get('totalMachines')?.invalid && medrForm.get('totalMachines')?.touched) {
                  <kendo-formerror>
                    <kendo-svg-icon
                      [icon]="exclamationTriangleIcon"
                      class="me-1"
                    ></kendo-svg-icon>
                    Total machines is required and must be at least 1
                  </kendo-formerror>
                }
              </kendo-formfield>
            </div>
          </div>
        </div>
      </div>
    }
  </form>
</div>
